import type { ReferralPolicy, Referral, CommissionRule } from '../types';

export function generateReferralCode(prefix: "RP" | "ST" | "SH"): string {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let result = '';
  for (let i = 0; i < 6; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return `${prefix}-${result}`;
}

export function buildReferralLink(code: string): string {
  return `/r/${code}`;
}

export function parseReferralCode(code: string): { prefix: string; isValid: boolean } {
  const match = code.match(/^(RP|ST|SH)-[A-Z0-9]{6}$/);
  return {
    prefix: match ? match[1] : '',
    isValid: !!match,
  };
}

export function evaluateCommissionEligibility(
  referral: Referral,
  policy: ReferralPolicy
): { eligible: boolean; reason?: string } {
  if (policy.blockSelfReferrals && 
      referral.source.kind === 'internal_staff' && 
      referral.fromDoctorId === referral.source.id) {
    return { eligible: false, reason: 'Self-referral blocked by policy' };
  }

  if (!policy.allowDoctorCommissions && 
      referral.source.kind === 'internal_staff') {
    return { eligible: true }; // Let commission rule determine if points are allowed
  }

  if (!policy.allowStakeholderPayouts && 
      referral.source.kind === 'stakeholder') {
    return { eligible: false, reason: 'Stakeholder payouts disabled by policy' };
  }

  return { eligible: true };
}

export function calculateCommissionAmount(
  rule: CommissionRule,
  estimatedAmount: number = 0
): number {
  let amount = 0;

  switch (rule.basis) {
    case 'percentage':
      amount = (estimatedAmount * rule.value) / 100;
      break;
    case 'flat':
      amount = rule.value;
      break;
    case 'points':
      amount = rule.value; // Points are just the value
      break;
  }

  if (rule.capPerReferral && amount > rule.capPerReferral) {
    amount = rule.capPerReferral;
  }

  return amount;
}

export function isDuplicateReferral(
  newReferral: { patientPhone?: string; dateCreated: string },
  existingReferrals: Referral[]
): boolean {
  if (!newReferral.patientPhone) return false;

  const newDate = new Date(newReferral.dateCreated).toDateString();
  
  return existingReferrals.some(existing => 
    existing.patientPhone === newReferral.patientPhone &&
    new Date(existing.dateCreated).toDateString() === newDate
  );
}

export function checkRateLimit(
  referrerId: string,
  recentReferrals: Referral[],
  limitPerHour: number
): { allowed: boolean; count: number } {
  const oneHourAgo = new Date(Date.now() - 60 * 60 * 1000);
  
  const recentCount = recentReferrals.filter(referral =>
    referral.source.id === referrerId &&
    new Date(referral.dateCreated) > oneHourAgo
  ).length;

  return {
    allowed: recentCount < limitPerHour,
    count: recentCount,
  };
}

export function generateQRPlaceholderUrl(code: string): string {
  return `data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="200" height="200"><rect width="200" height="200" fill="%23f0f0f0"/><text x="100" y="100" text-anchor="middle" dy=".3em" font-family="Arial" font-size="12">QR: ${code}</text></svg>`;
}

export function exportCommissionsToCSV(commissions: any[]): string {
  if (commissions.length === 0) return '';

  const headers = ['Date', 'Referrer', 'Type', 'Amount', 'Status', 'Reference'];
  const rows = commissions.map(commission => [
    new Date(commission.createdAt).toLocaleDateString(),
    commission.referrer.name || commission.referrer.id,
    commission.referrer.kind,
    commission.calculatedAmount.toString(),
    commission.payoutStatus,
    commission.reason || '',
  ]);

  const csvContent = [headers, ...rows]
    .map(row => row.map(field => `"${field}"`).join(','))
    .join('\n');

  return csvContent;
}
